import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import PLUVA_WAY_DRONE from '../../../../../assets/images/pluva_way_drone.jpg';
import { TelInput } from '../../../../../components/common/inputs/TelInput';
import { PopupConfirmModal } from '../../../../../components/common/modal/popup/PopupConfirmModal';
import { usePostCustomerInquiriesMutation } from '../../../../../hooks/api/inquiry/mutations/usePostCustomerInquiries.mutation';
import { useCheckUserLoggedIn } from '../../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../../hooks/user/useGetUserProfile';
import { useNotification } from '../../../../../provider/ToastNotificationProvider';
import { postMessageToRN } from '../../../../../utils/RNCommunicator/postMessageToRN.util';
import { SectionTitle } from './SectionTitle';
import { IProduct } from '../../../../../interfaces/Product.interfaces';

interface ProductPluvaWaySectionProps {
  product: IProduct;
}

export const ProductPluvaWaySection = ({ product }: ProductPluvaWaySectionProps) => {
  const [shouldShowSmsPopup, setShouldShowSmsPopup] = useState<boolean>(false);
  const [shouldShowSmsBlockedPopup, setShouldShowSmsBlockedPopup] = useState<boolean>(false);

  const [userPhoneNumber, setUserPhoneNumber] = useState<string>();
  const [requestCount, setRequestCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const telInputRef = useRef<HTMLInputElement | null>(null);
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();
  const { openNotification } = useNotification();

  const { mutate: postCustomerInquiresForPluvaWay } = usePostCustomerInquiriesMutation({
    onSuccess: () => {
      showSmsSentNotification();
      setShouldShowSmsPopup(false);
    },
  });

  const showSmsSentNotification = () => {
    openNotification({
      message: '문자 발송이 완료되었습니다.',
    });
  };

  const handleInquiryVisitScheduleButtonClick = () => {
    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:07041382499' });
    } else {
      window.location.href = 'tel:07041382499';
    }
    ampli.pvmClickPluvawayButton({ contact_method: 'phone' });
    ReactGA.event('pvm_click_pluvaway_button', {
      category: 'home',
      action: 'pvm_click_pluvaway_button',
      contact_method: 'phone',
    });
  };

  const sendSms = (phoneNumber: string) => {
    const { code, price, machine } = product;
    const { engineOutputInPs, model, brand } = machine || {};
    postCustomerInquiresForPluvaWay({
      reqBody: { category: 'PLUVA_WAY', phoneNumber, metadata: [code, price, engineOutputInPs, model, brand.name] },
    });
    ampli.pvmClickPluvawayButton({ contact_method: 'sms' });
    ReactGA.event('pvm_click_pluvaway_button', {
      category: 'home',
      action: 'pvm_click_pluvaway_button',
      contact_method: 'sms',
    });
  };

  const handleSendSmsButtonClick = () => {
    if (isUserLoggedIn && userProfile?.phoneNumber) {
      if (isBlocked) {
        setShouldShowSmsBlockedPopup(true);
        return;
      }
      sendSms(userProfile.phoneNumber);
      setRequestCount((prevCount) => prevCount + 1);
      return;
    }
    setShouldShowSmsPopup(true);
  };

  useEffect(() => {
    if (requestCount >= 3) {
      setIsBlocked(true);

      timerRef.current = setTimeout(() => {
        setIsBlocked(false);
        setRequestCount(0);
      }, 60000) as NodeJS.Timeout;
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [requestCount]);

  return (
    <>
      <Container>
        <SectionTitle>{'매물을 직접 보고 싶으시다면?'}</SectionTitle>

        <CardWrapper>
          <Card>
            <PluvaWayImageWrapper>
              <PluvaWayImage path={PLUVA_WAY_DRONE} />
            </PluvaWayImageWrapper>

            <BottomSection>
              <TextSection>
                <Title>{'플루바 웨이 (함평 메가 센터)'}</Title>
                <Location>{'전남 함평군 학교면 마산리 757-5'}</Location>
              </TextSection>

              <Description>
                {
                  '농업복합문화공간 플루바 웨이에서 매물을 직접 확인하고 시승 해보세요. 구매나 농업에 관련된 다양한 상담이 가능해요!'
                }
              </Description>

              <ButtonContainer>
                <Button onClick={handleSendSmsButtonClick}>
                  <ButtonText>{'문자 메세지로 주소 받기'}</ButtonText>
                  <BlueButton>
                    <svg
                      xmlns={'http://www.w3.org/2000/svg'}
                      width={'6'}
                      height={'9'}
                      viewBox={'0 0 6 9'}
                      fill={'none'}
                    >
                      <path
                        d={
                          'M1.04999 8.05002C0.899994 8.05002 0.749994 8.00002 0.649994 7.90002C0.449994 7.70002 0.499994 7.40002 0.699994 7.20002L4.24999 4.10002L0.699994 0.900025C0.499994 0.700025 0.449994 0.400025 0.649994 0.200025C0.849994 2.50041e-05 1.14999 -0.0499751 1.34999 0.150025L5.34999 3.65003C5.44999 3.75003 5.49999 3.90002 5.49999 4.05002C5.49999 4.20003 5.44999 4.35002 5.34999 4.45002L1.34999 7.95002C1.24999 8.00002 1.14999 8.05002 1.04999 8.05002Z'
                        }
                        fill={'#E6E9FF'}
                      />
                    </svg>
                  </BlueButton>
                </Button>
                <Button onClick={handleInquiryVisitScheduleButtonClick}>
                  <ButtonText>{'방문 일정 문의하기'}</ButtonText>
                  <BlueButton>
                    <svg
                      xmlns={'http://www.w3.org/2000/svg'}
                      width={'6'}
                      height={'9'}
                      viewBox={'0 0 6 9'}
                      fill={'none'}
                    >
                      <path
                        d={
                          'M1.04999 8.05002C0.899994 8.05002 0.749994 8.00002 0.649994 7.90002C0.449994 7.70002 0.499994 7.40002 0.699994 7.20002L4.24999 4.10002L0.699994 0.900025C0.499994 0.700025 0.449994 0.400025 0.649994 0.200025C0.849994 2.50041e-05 1.14999 -0.0499751 1.34999 0.150025L5.34999 3.65003C5.44999 3.75003 5.49999 3.90002 5.49999 4.05002C5.49999 4.20003 5.44999 4.35002 5.34999 4.45002L1.34999 7.95002C1.24999 8.00002 1.14999 8.05002 1.04999 8.05002Z'
                        }
                        fill={'#E6E9FF'}
                      />
                    </svg>
                  </BlueButton>
                </Button>
              </ButtonContainer>
            </BottomSection>
          </Card>
        </CardWrapper>
      </Container>

      <PopupConfirmModal
        isShow={shouldShowSmsBlockedPopup}
        hasCloseButton={false}
        description={'1분후에 문자 발송이 가능해요 🙂'}
        closeModal={() => setShouldShowSmsBlockedPopup(false)}
        buttonArray={[
          {
            text: '닫기',
            onClick: () => setShouldShowSmsBlockedPopup(false),
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={shouldShowSmsPopup}
        hasCloseButton={false}
        title={'플루바 웨이 주소 문자 발송'}
        closeModal={() => setShouldShowSmsPopup(false)}
        onClose={() => setUserPhoneNumber('')}
        description={'핸드폰 번호를 입력해주시면\n직접 매물을 확인 하실 수 있는\n플루바 웨이 주소를 전송해 드릴게요!'}
        element={
          <TelInput
            inputValue={userPhoneNumber}
            placeholder={'예) 01012341234'}
            onChange={(value: string | undefined) => setUserPhoneNumber(value)}
            inputRef={telInputRef}
          />
        }
        buttonArray={[
          {
            text: '발송',
            onClick: () => {
              if (userPhoneNumber) {
                sendSms(userPhoneNumber);
                setUserPhoneNumber('');
              }
            },
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => {
              setShouldShowSmsPopup(false);
              setUserPhoneNumber('');
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  padding: 32px var(--radius-large, 16px);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const CardWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Card = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  overflow: hidden;
`;

const PluvaWayImageWrapper = styled.div`
  height: 210px;

  display: flex;
  padding: var(--radius-large, 16px) 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-offblack, #505363);

  overflow: hidden;
`;

const PluvaWayImage = styled.div<{ path: string }>`
  height: 242px;
  flex-shrink: 0;
  align-self: stretch;

  background: ${(props) => `url(${props.path}) lightgray 50% / cover no-repeat`};
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
  padding: 24px;
`;

const TextSection = styled.div`
  width: 310px;
  height: 47px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const Location = styled.span`
  display: flex;
  height: 24px;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
  align-self: stretch;

  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const Description = styled.p`
  align-self: stretch;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const ButtonContainer = styled.div`
  width: 100%;

  padding-top: 8px;

  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Button = styled.button`
  display: flex;
  padding: var(--radius-medium, 8px) var(--radius-large, 16px);
  align-items: center;
  gap: 10px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
`;

const ButtonText = styled.span`
  flex: 1 0 0;

  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%;

  text-align: left;
`;

const BlueButton = styled.div`
  display: flex;
  padding: 6px 8px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);
`;
